import { useState, useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';

import Swal from 'sweetalert2';

import Button from '../../components/Button';
import ButtonBack from '../../components/ButtonBack';
import { FormGroup, Label } from '../../components/Form';
import ErrorMessage from '../../components/Form/ErrorMessage';
import Input from '../../components/Form/Input';
import Loading from '../../components/Loading';
import Spacing from '../../components/Spacing';
import Text from '../../components/Text';
import registerAddressSchema from '../../utils/schemas/registerAddress';

import * as cep from 'cep-promise';

import { Container } from './styles';
import { createAddress, login, createUser, logout } from '../../services/user';
import ErrorBox from '../../components/ErrorBox';
import m from '../../utils/messages';
import {
  clearEmailsValidated,
  clearCpfsValidated,
} from '../../utils/schemas/registerCpf';

const BRAZIL_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

const PENDING_USER_STORAGE_KEY = '@pendingUserRegistration@';

const INITIAL_FORM_VALUES = { 
  address_description: '', 
  postal_code: '', 
  district: '', 
  city: '', 
  neighborhood: '',
  address: '', 
  address_number: '',
  address2: '',
};

const RegisterAddress = () => {
  const history = useHistory();
  const locationState = history.location.state;

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [pendingRegistration, setPendingRegistration] = useState(() => {
    if (locationState?.userPayload) {
      return locationState;
    }

    const stored = sessionStorage.getItem(PENDING_USER_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  });

  const documentType = pendingRegistration?.userPayload?.is_company ? 'CNPJ' : 'CPF';

  useEffect(() => {
    if (locationState?.userPayload) {
      setPendingRegistration(locationState);
    }
  }, [locationState]);

  useEffect(() => {
    if (!pendingRegistration?.userPayload) {
      history.replace('/register');
    } else {
      sessionStorage.setItem(
        PENDING_USER_STORAGE_KEY,
        JSON.stringify(pendingRegistration)
      );
    }
  }, [history, pendingRegistration]);

  const handleCreateUserError = (err) => {
    let errorMessage = "Algo deu errado, tente novamente!";
    let errorTitle = "Atenção";

    if (err?.response?.data?.errors) {
      if (err.response.data.errors.document) {
        const docErrors = err.response.data.errors.document;

        if (docErrors.some(msg => msg.includes("já está sendo utilizado") || msg.includes("já está em uso"))) {
          errorTitle = `${documentType || "Documento"} já cadastrado`;
          errorMessage = `Este ${documentType || "documento"} já está cadastrado em nossa base de dados. Por favor, tente fazer login ou recuperar sua senha.`;
        } else if (docErrors.some(msg => msg.includes("inválido"))) {
          errorTitle = `${documentType || "Documento"} inválido`;
          errorMessage = `O ${documentType || "documento"} informado não é válido. Por favor, verifique os dados e tente novamente.`;
        } else {
          errorMessage = docErrors.join('<br>');
        }
      } else {
        errorMessage = Object.values(err.response.data.errors)
          .flat()
          .join('<br>');
      }
    } else if (err?.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err?.message) {
      errorMessage = err.message;
    }

    Swal.fire({
      icon: "error",
      title: errorTitle,
      html: errorMessage,
      confirmButtonColor: "#CB3D42",
      confirmButtonText: "OK"
    });
  };

  const handleSubmit = async (formData, { setSubmitting }) => {
    setSubmitting(true);

    if (!pendingRegistration?.userPayload) {
      Swal.fire({
        icon: "error",
        title: "Sessão expirada",
        text: "Não encontramos os dados do cadastro. Por favor, preencha novamente.",
        confirmButtonColor: "#CB3D42",
      });
      setSubmitting(false);
      history.replace('/register');
      return;
    }

    try {
      await logout();
    } catch { }

    let addressToken = null;

    try {
      const responseData = await createUser(pendingRegistration.userPayload);
      clearEmailsValidated();
      clearCpfsValidated();
      const payload = responseData?.data;
      if (!payload) {
        throw new Error('Não foi possível concluir o cadastro do usuário.');
      }

      addressToken =
        payload?.token ||
        payload?.application_token ||
        payload?.ApplicationToken ||
        responseData?.token;

      if (!addressToken) {
        throw new Error('Não conseguimos obter autorização para cadastrar o endereço.');
      }
    } catch (userErr) {
      handleCreateUserError(userErr);
      setSubmitting(false);
      return;
    }

    const addressPayload = {
      ...formData,
      country: 'BRA',
      address_type: 'delivery',
      landmark: 'N/A',
      postal_code: formData.postal_code?.replaceAll('-', '') || '',
    };

    try {
      await createAddress(addressPayload, addressToken);
    } catch (addressErr) {
      console.log(addressErr);
      Swal.fire({
        icon: "error",
        title: "Erro ao cadastrar endereço",
        text: addressErr?.response?.data?.message || "Não foi possível salvar o endereço. Tente novamente.",
        confirmButtonColor: "#CB3D42",
      });
      setSubmitting(false);
      setError(m('internalError'));
      return;
    }

    sessionStorage.removeItem(PENDING_USER_STORAGE_KEY);
    setSuccess(true);

    await Swal.fire({
      title: 'Cadastro realizado com sucesso!',
      icon: 'success',
      timer: 5000,
      confirmButtonText: 'OK',
    });

    try {
      const credentials = pendingRegistration.credentials;
      if (credentials?.email && credentials?.password) {
        const loginResponse = await login(credentials);

        if (loginResponse?.redirectTo) {
          window.location.href = loginResponse.redirectTo;
          return;
        }
      }
      history.push('/');
    } catch (err) {
      console.log(err);
      setError(m('internalError'));
    }

    setSubmitting(false);
  };

  const buildBackFormState = () => {
    if (pendingRegistration?.formValues) {
      return pendingRegistration.formValues;
    }

    if (locationState?.formValues) {
      return locationState.formValues;
    }

    return null;
  };

  const handleBackToForm = () => {
    const backFormValues = buildBackFormState();

    if (backFormValues) {
      history.push('/register', { ...backFormValues, resumeStep: 2 });
      return;
    }

    history.push('/register');
  };

  return (
    <>
      <ButtonBack
        onClick={handleBackToForm}
        title="Cadastro"
        totalSteps={3}
        step={3}
      />
      <Container>
        {success && <Loading position="fixed" text="Aguarde" />}
        <ErrorBox error={error} />
        <Formik
          initialValues={INITIAL_FORM_VALUES}
          validationSchema={registerAddressSchema}
          validateOnMount
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <AddressForm>
              <Button
                variant="buttonPrimary"
                text="Finalizar cadastro"
                type="submit"
                disabled={!formikProps.isValid || formikProps.isSubmitting}
                loading={formikProps.isSubmitting}
                style={{ width: '100%', marginTop: 20 }}
              />
            </AddressForm>
          )}
        </Formik>
        <Text variant="support" style={{ textAlign: 'justify' }}>
          Ao se cadastrar, você concorda com nossa{' '}
          <a target="_blank" rel="noopener noreferrer" href="https://www.zonasul.com.br/politica-de-privacidade">
            <span className="semibold">
              política de privacidade
            </span>
          </a>
        </Text>
      </Container>
    </>
  );
};

const AddressForm = ({ children }) => {
  const [loadingSearch, setLoadingSearch] = useState(false);

  const formik = useFormikContext();
  const { values, setFieldValue } = formik;

  const clearAddress = () => {
    formik.setFieldValue('district', '');
    formik.setFieldValue('city', '');
    formik.setFieldValue('neighborhood', '');
    formik.setFieldValue('address', '');
    formik.setFieldValue('address_number', '');
    formik.setFieldValue('address2', '');
  };

  const searchAddressInfo = async (e) => {
    setLoadingSearch(true);

    const searchTerm = e.target.value.replace(/[^0-9]/g, '');

    if (searchTerm.length < 8) {
      clearAddress();
      setLoadingSearch(false);
      return;
    }

    try {
      const result = await cep(searchTerm);
      formik.setFieldValue('district', (result.state || '').toUpperCase());
      formik.setFieldValue('city', result.city || '');
      formik.setFieldValue('neighborhood', result.neighborhood || '');
      formik.setFieldValue('address', result.street || '');
    } catch (err) {
      clearAddress();
    }

    setLoadingSearch(false);
  };

  return (
    <Form className="form login__form">
      {loadingSearch && <Loading text="Buscando Endereço..." />}
      <Text
        variant="regular"
        style={{
          fontSize: "16px",
          fontWeight: 500,
          marginBottom: "12px"
        }}
      >
        Complete seu cadastro e aproveite o melhor do{" "}
        <span style={{ color: "#CB3D42", fontWeight: 600 }}>Zona Sul</span>:
      </Text>
      <Spacing size={20} />
      <FormGroup>
        <Input
          name="postal_code"
          mask="DDDDD-DDD"
          type="text"
          label="CEP"
          onBlur={searchAddressInfo}
          autoFocus
        />
        <ErrorMessage name="postal_code" />
      </FormGroup>
      <Spacing size={20} />
      <FormGroup>
        <Input name="address" type="text" label="Endereço" />
        <ErrorMessage name="address" />
      </FormGroup>
      <Spacing size={20} />
      <FormGroup>
        <Input name="address_number" type="text" label="Número do endereço" />
        <ErrorMessage name="address_number" />
      </FormGroup>
      <Spacing size={20} />
      <FormGroup>
        <Input name="neighborhood" type="text" label="Bairro" />
        <ErrorMessage name="neighborhood" />
      </FormGroup>
      <Spacing size={20} />
      <FormGroup>
        <Input name="city" type="text" label="Cidade" />
        <ErrorMessage name="city" />
      </FormGroup>
      <Spacing size={20} />
      <FormGroup>
        <Label htmlFor="state-select">Estado</Label>
        <select
          id="state-select"
          name="district"
          value={values.district || ''}
          onChange={(event) => setFieldValue('district', event.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '3px',
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            color: '#161414',
            fontSize: '14px',
            appearance: 'none',
          }}
        >
          <option value="">Selecione</option>
          {BRAZIL_STATES.map((state) => (
            <option key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </select>
        <ErrorMessage name="district" />
      </FormGroup>
      <Spacing size={20} />
      <FormGroup>
        <Input name="address2" type="text" label="Complemento (opcional)" />
        <ErrorMessage name="address2" />
      </FormGroup>
      <Spacing size={20} />
      <FormGroup>
        <Input name="address_description" type="text" label="Nome do endereço (ex: Casa)" />
        <ErrorMessage name="address_description" />
      </FormGroup>
      <Spacing size={20} />
      {children}
    </Form>
  );
};

export default RegisterAddress;
